import { apiErrorField } from '@agos/api-client'
import {  } from '@agos/customer-core'
import { formatCents, formatCount, parseMoneyToCents, tt } from '@agos/shared'

/**
 * The two mini-games — the wheel and the scratch card — and what a go is worth.
 *
 * `GET /mini-game/status` answers a state per game: `available`, `isActive`, `nextPlayAt`, `playsToday`,
 * `frequencyPerDay`. **Measured on this data both are `isActive: false`**, because no campaign of either
 * type is configured, so the screen's ordinary condition today is *not running* rather than *ready*.
 * Three states, not two: a game that is switched off is not the same as one already played, and telling
 * a customer to come back tomorrow for something that will never run is worse than saying it is off.
 *
 * **`POST /mini-game/play` awards real prizes.** It adds points to the customer's balance and inserts a
 * single-use coupon row. Nothing here calls it speculatively, and the e2e intercepts it like every other
 * write.
 *
 * **A refusal is a 429 carrying `nextPlayAt`**, and the server says why it bothered: *"the screen counts
 * down to it, and a bare 'no' leaves it counting down to nothing."* That field is read rather than
 * guessed at, and when it is absent the page says *tomorrow* rather than inventing a clock.
 */

export type GameKey = 'click_and_win' | 'scratch_card'

export type GameState =
  /** No campaign of this type is running. */
  | { kind: 'off' }
  /** A go is available now. */
  | { kind: 'ready'; playsLeft: number }
  /** Today's allowance is used up. */
  | { kind: 'spent'; nextPlayAt: string | null }

export interface GameStatus {
  key: GameKey
  state: GameState
}

function stateOf(row: Record<string, unknown>): GameState {
  if (row.isActive !== true) return { kind: 'off' }
  const allowance = numberOr(row.frequencyPerDay, 1)
  const played = numberOr(row.playsToday, 0)
  if (row.available === true) {
    return { kind: 'ready', playsLeft: Math.max(0, allowance - played) }
  }
  return {
    kind: 'spent',
    nextPlayAt: typeof row.nextPlayAt === 'string' && row.nextPlayAt !== '' ? row.nextPlayAt : null,
  }
}

function numberOr(value: unknown, fallback: number): number {
  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

export function statusOf(payload: unknown): GameStatus[] {
  const row = (typeof payload === 'object' && payload !== null ? payload : {}) as Record<string, unknown>
  return (['click_and_win', 'scratch_card'] as const).map((key) => {
    const game = row[key]
    if (typeof game !== 'object' || game === null) return { key, state: { kind: 'off' } }
    return { key, state: stateOf(game as Record<string, unknown>) }
  })
}

export function mayPlay(state: GameState): boolean {
  return state.kind === 'ready'
}

/**
 * The body `POST /mini-game/play` takes — §9.266, owner O8.
 *
 * *"make sure the games are actually working… what about klikni a vyhraj, stieracia karta."*
 *
 * **Neither worked, and the reason is one word.** The page sent `{gameKey}`; `MiniGameService.play`
 * reads `body.get("type")`, finds nothing, and refuses with *Neznámy typ hry* — a **400 on every
 * press of either game**. Legacy sends `{type: "scratch_card"}`, which is where the name comes from.
 *
 * **Nothing could have caught it.** The request body is `{[key: string]: unknown}` in the generated
 * client, so the compiler has nothing to check against; the e2e stubs this endpoint because a real
 * call awards points and mints a coupon; and both campaigns are `is_active: false` on this data, so
 * the button that would have sent it has never been on screen. A function with a test is the only
 * place this can be pinned, which is why the payload is built here rather than inline at the call.
 */
export function playPayload(key: GameKey): Record<string, string> {
  return { type: key }
}

/**
 * The games worth putting on the screen at all — §9.264, owner O6.
 *
 * *"When the game is not running, in customer profile, remove it from it… it should be visible only
 * when game is running."*
 *
 * **Legacy does exactly this and says it in one line, twice**: `MojProfil.tsx:229` and `:397` are both
 * `if (!status?.isActive) return null`. AGOS drew a card either way with *Momentálne neprebieha* under
 * it — §9.246's doing, which put the games where the owner asked and kept a state legacy never shows.
 *
 * `spent` stays: a customer who has used today's go is being told when the next one is, which is a
 * running game rather than an absent one. Only `off` goes.
 */
export function runningGames(games: readonly GameStatus[]): GameStatus[] {
  return games.filter((game) => game.state.kind !== 'off')
}

/**
 * How much of the scratch card has been rubbed away, as a percentage — §9.265, owner O7.
 *
 * Legacy's arithmetic, kept: the scratched points are counted on a 4 px lattice against
 * `(width / 4) × (height / 4)` cells, and the card submits itself at **60 %**. A different threshold
 * or a different lattice would make the card feel different from the one customers know.
 */
export const SCRATCH_THRESHOLD = 60

export function scratchPercent(scratched: number, width: number, height: number): number {
  const cells = (width / 4) * (height / 4)
  if (!Number.isFinite(cells) || cells <= 0) return 0
  return Math.round(Math.min(100, (scratched / cells) * 100))
}

/** The lattice points a single rub covers, as legacy walks them: a disc of radius 22, every 4 px. */
export function scratchedPoints(centreX: number, centreY: number, radius = 22): string[] {
  const points: string[] = []
  for (let offsetX = -radius; offsetX <= radius; offsetX += 4) {
    for (let offsetY = -radius; offsetY <= radius; offsetY += 4) {
      if (offsetX * offsetX + offsetY * offsetY <= radius * radius) {
        points.push(`${Math.floor(centreX + offsetX)},${Math.floor(centreY + offsetY)}`)
      }
    }
  }
  return points
}

/** Whether the card has been scratched enough to reveal itself. */
export function scratchedEnough(percent: number): boolean {
  return percent >= SCRATCH_THRESHOLD
}

/**
 * What each game is called — §9.246.
 *
 * Legacy names them on their own cards: *Klikni a vyhraj* (`MojProfil.tsx:241`) and *Stierací los*
 * (`:409`). AGOS had them behind a shared *Hry* heading with no title of their own, so a card without
 * the heading would have been two unlabelled buttons.
 */
export function gameTitle(key: GameKey): string {
  return key === 'click_and_win' ? tt('Klikni a vyhraj') : tt('Stierací los')
}

/** Legacy's own two, on its own two cards. */
export function gameEmoji(key: GameKey): string {
  return key === 'click_and_win' ? '🎡' : '🎫'
}

/** What the screen says instead of a button. */
export function gameNote(state: GameState): string {
  if (state.kind === 'off') return tt('Táto hra teraz nebeží.')
  if (state.kind === 'ready') return ''
  return state.nextPlayAt === null
    ? tt('Dnešný pokus ste už využili. Skúste to zajtra.')
    : tt('Dnešný pokus ste už využili.')
}

// ─── what came out of a go ───────────────────────────────────────────────────

export interface Prize {
  won: boolean
  rewardType: string
  rewardLabel: string | null
  /** Points for a `points` prize; euros for a fixed coupon; per cent for `coupon_percent`. */
  rewardValue: number | null
  couponCode: string | null
  pointsAwarded: number
}

/**
 * The result of a go.
 *
 * The reward's kind arrives under **two spellings** — the editor writes `rewardType`, older rows carry
 * `type` — and the server itself reads whichever is present, defaulting to `none`. The client does the
 * same, because a prize this build fails to recognise would be shown as a loss.
 */
export function prizeFrom(payload: unknown): Prize | null {
  if (typeof payload !== 'object' || payload === null) return null
  const row = payload as Record<string, unknown>
  const kind =
    typeof row.rewardType === 'string' && row.rewardType !== ''
      ? row.rewardType
      : typeof row.type === 'string' && row.type !== ''
        ? row.type
        : 'none'
  const value = row.rewardValue === null || row.rewardValue === undefined ? null : Number(row.rewardValue)
  return {
    won: row.won === true,
    rewardType: kind,
    rewardLabel: typeof row.rewardLabel === 'string' && row.rewardLabel !== '' ? row.rewardLabel : null,
    rewardValue: value !== null && Number.isFinite(value) ? value : null,
    couponCode: typeof row.couponCode === 'string' && row.couponCode !== '' ? row.couponCode : null,
    pointsAwarded: numberOr(row.pointsAwarded, 0),
  }
}

/**
 * What the customer is told they won.
 *
 * The server's own `rewardLabel` first, because it is what the restaurant wrote on the wheel. Only if
 * there is none does this describe the prize from its parts — and a kind this build does not know is
 * named rather than shown as nothing, which is FB-166's lesson again.
 */
/**
 * The headline over a result — §9.271, owner O16.
 *
 * Legacy's two words, because they are the two the customers already know: *Výhra!* or *Tentokrát
 * nie...* over an emoji. The old screen had neither, and put the outcome into the same 12 px grey
 * line as everything else.
 */
export function prizeHeadline(prize: Prize | null): string {
  if (prize === null) return ''
  return prize.won ? tt('Výhra!') : tt('Tentokrát nie...')
}

export function prizeEmoji(prize: Prize | null): string {
  if (prize === null) return ''
  return prize.won ? '🎉' : '😔'
}

/**
 * The code, when there is one to type — §9.271, owner O15.
 *
 * `prizeFrom` has parsed `couponCode` since it was written and **nothing rendered it**, while
 * `prizeNote` returned the slot's own label first and every slot in both campaigns has one. So a
 * customer who won a coupon read *5% zlava* and never saw the code the checkout wants.
 */
export function prizeCoupon(prize: Prize | null): string | null {
  if (prize === null || !prize.won) return null
  return prize.couponCode
}

/** What was credited to the account, for a prize that is points rather than a code. */
export function prizePointsNote(prize: Prize | null): string {
  if (prize === null || !prize.won || prize.pointsAwarded <= 0) return ''
  return `+${formatCount(prize.pointsAwarded)} ${tt('bodov pripísaných na váš účet')}`
}

/**
 * Whether the *you have used today\'s go* note should be said at all — §9.271, owner O14.
 *
 * It rendered beside a fresh prize, so the first thing somebody read after winning was that they could
 * not play. It is true and it is not the news; it comes back on the next visit, when there is no prize
 * on screen and it is the only thing left to say.
 */
export function shouldSaySpent(hasPrize: boolean, state: GameState): boolean {
  return !hasPrize && state.kind !== 'ready'
}

export function prizeNote(prize: Prize | null): string {
  if (prize === null) return ''
  if (!prize.won) return tt('Tentokrát nič. Skúste to znova zajtra.')
  if (prize.rewardLabel !== null) return prize.rewardLabel
  if (prize.rewardType === 'points') return `${formatCount(prize.pointsAwarded)} ${tt('bodov')}`
  if (prize.rewardType === 'coupon_percent' && prize.rewardValue !== null) {
    return `${formatCount(prize.rewardValue)} % ${tt('zľava')}`
  }
  if (prize.rewardType === 'coupon' && prize.rewardValue !== null) {
    return `${formatCents(parseMoneyToCents(prize.rewardValue))} ${tt('zľava')}`
  }
  return `${tt('Výhra')}: ${prize.rewardType}`
}

/** The refusal's `nextPlayAt`, which the server sends precisely so the screen has something to say. */
export function refusedUntil(failure: unknown): string | null {
  return apiErrorField(failure, 'nextPlayAt')
}
